import { ReactNode, RefObject, useCallback } from 'react'
import { FullMoneyflowType, MoneyflowType } from 'interfaces/moneyflow'
import { FullAccountType } from 'interfaces/accounts'
import { CategorizedPage, GenericPage, InsightsPageType, InternalPageType, PageType, TimeframedPage, AccountPage } from 'interfaces/pages'
import { Anchor, AnchorPlacement } from 'interfaces/navigation'
import {
  FULL_ACCOUNT_PAGES,
  CREDIT_PAGE,
  DASHBOARD_PAGE,
  INTERNAL_PAGE_DEFINITIONS,
  INTERNAL_PAGE_FIRST_LEVEL_URLS,
  INTERNAL_PAGE_URLS_SET,
  FULL_MONEYFLOW_PAGES,
  NETWORTH_PAGE,
  ONBOARDING_SUMMARY,
  OPPORTUNITIES_PAGE,
  PAGE_DEFINITIONS_MAP,
  TIMEFRAMED_PAGES,
  URL_PAGE_MAP,
  RETURN_URL_PARAM_NAME,
  MONEYFLOW_PAGES,
  ACCOUNT_PAGES,
} from 'constants/pages'
import {
  CREDIT_LINKAGE_URL,
  OFFERS_URL,
  REDEMPTION_ENTRY_URL,
  SETTINGS_ACCOUNTS_URL,
  FEED_POSTS_BASE_URL,
  FEED_AUTHORS_BASE_URL,
  FEED_TAGS_BASE_URL,
  DASHBOARD_URL,
} from 'tangle-shared'
import { OnboardingInternalStep, OnboardingProcessInternalStep, OnboardingStep } from 'interfaces/member'
import { ONBOARDING_STEP_ANCHOR_BY_NAME, ONBOARDING_STEP_BY_URL, ONBOARDING_STEP_NUMBERS } from 'constants/onboarding'
import SeeAllCategoriesButton from 'components/atoms/buttons/SeeAllCategoriesButton'
import { captureWarning } from 'helpers/errorHelpers'
import { transformRelativeUrlToPathname } from 'helpers/utilsHelpers'
import { useContext } from 'react'
import { DEFAULT_PAGE_CONTEXT } from 'constants/pageContext'
import { PageContext } from 'components/features/pfm/PageContext/PageContext'
import { defaultMemoize } from 'reselect'

export const isMoneyflowPage = <TFull extends true>(page: PageType, full?: TFull): page is (typeof full extends Extract<TFull, true> ? FullMoneyflowType : MoneyflowType) =>
  (full ? FULL_MONEYFLOW_PAGES : MONEYFLOW_PAGES).includes(page as any)

export const isAccountPage = (page: PageType): page is FullAccountType =>
  FULL_ACCOUNT_PAGES.includes(page as any)

export const isGenericPage = (page: PageType): page is GenericPage =>
  page === CREDIT_PAGE || page === NETWORTH_PAGE

export const isCategorizedPage = (page: PageType): page is CategorizedPage =>
  isMoneyflowPage(page, true) || ACCOUNT_PAGES.includes(page as AccountPage)

export const isTimeframedPage = (page: PageType): page is TimeframedPage =>
  TIMEFRAMED_PAGES.includes(page as any)

export const isInsightsPage = (page: PageType): page is InsightsPageType =>
  page === DASHBOARD_PAGE || page === OPPORTUNITIES_PAGE || isTimeframedPage(page)

export const isDashboardPage = () => {
  return location.pathname === DASHBOARD_URL
}

export const getAnchorPage = (anchor: Anchor): PageType | null => {
  const placement: AnchorPlacement = typeof anchor === 'string' ? anchor : anchor['anchor']
  return (placement.split('_')[0] || null) as PageType | null
}

export const getAnchorBlock = (anchor: Anchor): string | null => {
  const placement: AnchorPlacement = typeof anchor === 'string' ? anchor : anchor['anchor']
  return (placement.split('_')[1] || null) as PageType | null
}

export const getAnchorUrl = (to: Anchor): string => {
  const anchor = typeof to === 'string'
    ? to
    : to.anchor
  const page = getAnchorPage(anchor) || DASHBOARD_PAGE
  const pageDefinition = PAGE_DEFINITIONS_MAP[page]

  // FRO-2177
  if (!pageDefinition) {
    captureWarning('There is no page definition', { to, page })
    return PAGE_DEFINITIONS_MAP[DASHBOARD_PAGE].url
  }

  const url = pageDefinition.url

  return typeof to === 'string'
    ? url
    : `${ url }/${ to.id }`
}

export const getAnchorByUrl = (url: string): PageType =>
  URL_PAGE_MAP[url]

export const extractAnchor = (to: Anchor): AnchorPlacement | string => {
  return typeof to === 'string'
    ? to
    : `${ to.anchor }/${ to.id }`
}

export const isInternalUrl = (pathname: string): boolean =>
  INTERNAL_PAGE_URLS_SET.has(pathname)
  || INTERNAL_PAGE_FIRST_LEVEL_URLS.some((url) => new RegExp(`${ url }/.+`).test(pathname))

const INTERNAL_PAGES_SET: Set<PageType> = new Set(INTERNAL_PAGE_DEFINITIONS.map((def) => def.name))
export const isInternalPage = (page: PageType): page is InternalPageType =>
  INTERNAL_PAGES_SET.has(page)

// ===========================================================================//
//                                   SETTINGS                                 //
// ===========================================================================//

export const SIDEBAR_FIRST_LEVEL_URLS_SET = new Set([
  CREDIT_LINKAGE_URL,
])

export const SIDEBAR_SECOND_LEVEL_URLS_SET = new Set([
  SETTINGS_ACCOUNTS_URL,
])

const SIDEBAR_STARTING_URLS = [
  OFFERS_URL,
  REDEMPTION_ENTRY_URL,
  FEED_POSTS_BASE_URL,
  FEED_AUTHORS_BASE_URL,
  FEED_TAGS_BASE_URL,
]

export const isShrunkSidebarUrl = (url: string, isAuthenticated: boolean) => {
  if (!isAuthenticated) {
    // never shrunk sidebar if user is not authenticated
    return false
  }

  const pathname = transformRelativeUrlToPathname(url)
  return false
    || SIDEBAR_FIRST_LEVEL_URLS_SET.has(pathname)
    || SIDEBAR_SECOND_LEVEL_URLS_SET.has(pathname)
    || SIDEBAR_STARTING_URLS.some((urlStart) => pathname.startsWith(urlStart))
}

export const isInternalPageUrlWithSidebar = (url: string, isAuthenticated: boolean): boolean => {
  const page = URL_PAGE_MAP[url]
  return isInsightsPage(page) || isShrunkSidebarUrl(url, isAuthenticated)
}

// ===========================================================================//
//                                   ONBOARDING                               //
// ===========================================================================//

export const getOnboardingStepByUrl = (url: string): OnboardingStep | null =>
  ONBOARDING_STEP_BY_URL[url] || null

const ONBOARDING_INTERNAL_PROCESS_STEP = new Set<OnboardingStep>(['property'])
export const isOnboardingInternalProcessStep = (step: OnboardingStep): step is OnboardingProcessInternalStep =>
  ONBOARDING_INTERNAL_PROCESS_STEP.has(step)

export const isInternalOnboardingStepUrl = (url: string): boolean => {
  const step = getOnboardingStepByUrl(url)
  return step !== null && isOnboardingInternalProcessStep(step)
}

const isOnboardingStepAllowed = (currentStep: OnboardingStep, allowedStep: OnboardingStep) => {
  if (allowedStep === 'finished') {
    // allow only summary and register pages
    return !isOnboardingInternalProcessStep(currentStep)
  }

  const currentNumber = ONBOARDING_STEP_NUMBERS[currentStep]
  const allowedNumber = ONBOARDING_STEP_NUMBERS[allowedStep]
  return currentNumber <= allowedNumber
}

type GetRedirectAnchorParams = {
  url: string
  allowedStep: OnboardingInternalStep
  previousStep: OnboardingInternalStep | null
  isAuthenticated: boolean
}

/**
 * Return AnchorPlacement for redirect if user's not allowed to passed internal url
 * or return null otherwise
 */
const ALLOWED_FINISH_PREV_STEPS = new Set<OnboardingStep | null>(['property'])
export const getRedirectAnchor = ({ url, allowedStep, previousStep, isAuthenticated }: GetRedirectAnchorParams): AnchorPlacement | null => {
  if (!isAuthenticated) {
    return 'login'
  }

  const pathname = (new URL(url, 'https://statusmoney.com/')).pathname
  const currentStep = getOnboardingStepByUrl(pathname)
  if (!currentStep) {
    // don't pass user to internal pages when they didn't pass onboarding
    return allowedStep === 'finished' ? null : ONBOARDING_STEP_ANCHOR_BY_NAME[allowedStep] ?? null
  }

  const isLegacyStep = !ONBOARDING_STEP_NUMBERS[allowedStep]

  if (isLegacyStep || !isOnboardingStepAllowed(currentStep, allowedStep)) {
    return (ALLOWED_FINISH_PREV_STEPS.has(previousStep) && allowedStep === 'finished') || isLegacyStep
      ? ONBOARDING_SUMMARY
      : ONBOARDING_STEP_ANCHOR_BY_NAME[allowedStep] ?? null
  }

  return null
}

// TODO implement base page component with this method
export const createSeeAllCategoriesButton = (placement: AnchorPlacement): ReactNode => (
  <SeeAllCategoriesButton
    placement={ placement }
  />
)

export const useShouldShowPageParamsStickyHeader = (staticHeaderRef: RefObject<HTMLDivElement | null>) => {
  return useCallback(() => {
    if (!staticHeaderRef.current) {
      // fallback if ref's broken
      return window.pageYOffset >= 300
    }
    return staticHeaderRef.current.getBoundingClientRect().bottom < 0
  }, [staticHeaderRef])
}

export const addReturnUrlIfExisting = (url: string): string => {
  const { searchParams } = new URL(document.location.href)
  const returnUrl = searchParams.get(RETURN_URL_PARAM_NAME)

  return returnUrl
    ? `${ url }?${ RETURN_URL_PARAM_NAME }=${ encodeURIComponent(returnUrl) }`
    : url
}

export const usePageContext = () => {
  return useContext(PageContext) || DEFAULT_PAGE_CONTEXT
}

export const getPageName = defaultMemoize((location: Pick<URL, 'pathname'> | string): PageType | null => {
  if (typeof location === 'string') {
    location = new URL(location, document.baseURI)
  }
  const { pathname } = location
  const pageName = URL_PAGE_MAP[pathname]

  if (pageName) {
    return pageName
  }

  return null
})

export const useIsPublicPage = () => {
  const { isPublicPage } = usePageContext()
  return isPublicPage
}


